import React, { useState, useEffect } from 'react';
import TrialCountdown from '../components/TrialCountdown';
import { 
  BookOpen, 
  Plus, 
  Edit3, 
  Trash2, 
  Calendar, 
  Clock, 
  Heart, 
  Brain,
  Sparkles,
  Star,
  Zap,
  Target,
  Shield,
  Activity,
  BarChart3,
  TrendingUp,
  Lightbulb,
  Eye,
  EyeOff,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  AlertTriangle,
  PenTool,
  X,
  Save,
  Loader,
  Tag,
  Lock,
  Unlock,
  ArrowRight
} from 'lucide-react';
import { journalApi } from '../utils/journalApi';
import JournalInsights from '../components/JournalInsights';

const Journal = () => {
  const [entries, setEntries] = useState([]);
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [generatingInsights, setGeneratingInsights] = useState({});
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    mood: 'neutral',
    tags: '',
    isPrivate: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Load journal entries on component mount
  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const entriesData = await journalApi.getEntries();
      setEntries(entriesData);
      
      // Load insights for all entries
      const insightsData = {};
      for (const entry of entriesData) {
        try {
          const insight = await journalApi.getInsights(entry._id);
          insightsData[entry._id] = insight;
        } catch (error) {
          // No insights found for this entry, which is fine
        }
      }
      setInsights(insightsData);
    } catch (error) {
      console.error('Error loading journal entries:', error);
      setError('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }
    
    if (formData.content.length > 10000) {
      newErrors.content = 'Content must be less than 10,000 characters';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      const entryData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      };

      if (editingEntry) {
        // Update existing entry
        const updatedEntry = await journalApi.updateEntry(editingEntry._id, entryData);
        setEntries(prev => prev.map(entry => 
          entry._id === editingEntry._id ? updatedEntry : entry
        ));
      } else {
        // Create new entry
        const newEntry = await journalApi.createEntry(entryData);
        setEntries(prev => [newEntry, ...prev]);
      }
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        mood: 'neutral',
        tags: '',
        isPrivate: true
      });
      setShowNewEntryForm(false);
      setEditingEntry(null);
      setFormErrors({});
    } catch (error) {
      console.error('Error saving journal entry:', error);
      setFormErrors({ submit: 'Failed to save journal entry. Please try again.' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title || '',
      content: entry.content || '',
      mood: entry.mood || 'neutral',
      tags: entry.tags ? entry.tags.join(', ') : '',
      isPrivate: entry.isPrivate !== undefined ? entry.isPrivate : true
    });
    setShowNewEntryForm(true);
    setFormErrors({});
  };

  const handleCancelForm = () => {
    setShowNewEntryForm(false);
    setEditingEntry(null);
    setFormData({
      title: '',
      content: '',
      mood: 'neutral',
      tags: '',
      isPrivate: true
    });
    setFormErrors({});
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this journal entry? This action cannot be undone.')) {
      return;
    }

    try {
      await journalApi.deleteEntry(entryId);
      setEntries(prev => prev.filter(entry => entry._id !== entryId));
      
      // Remove insights for this entry
      setInsights(prev => {
        const newInsights = { ...prev };
        delete newInsights[entryId];
        return newInsights;
      });
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      setError('Failed to delete journal entry');
    }
  };

  const handleGenerateInsights = async (entryId) => {
    try {
      setGeneratingInsights(prev => ({ ...prev, [entryId]: true }));
      const insight = await journalApi.generateInsights(entryId);
      setInsights(prev => ({ ...prev, [entryId]: insight }));
    } catch (error) {
      console.error('Error generating insights:', error);
      setError('Failed to generate AI insights');
    } finally {
      setGeneratingInsights(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMoodIcon = (mood) => {
    const moodIcons = {
      'very-low': '😔',
      'low': '😕',
      'neutral': '😐',
      'good': '🙂',
      'excellent': '😊'
    };
    return moodIcons[mood] || '😐';
  };

  const truncateContent = (content, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Your Journal...</h3>
          <p className="text-gray-600">Preparing your personal reflection space</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
        {/* Trial Countdown */}
        <TrialCountdown />
        
        {/* Beautiful Header - Mobile Responsive */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-xl border border-blue-100 p-4 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-lg flex-shrink-0">
                <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 leading-tight">Personal Journal</h1>
                <p className="text-sm sm:text-xl text-gray-600 leading-relaxed">Record your thoughts and get AI insights</p>
              </div>
            </div>
            <button 
              onClick={() => setShowNewEntryForm(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-4 sm:px-6 py-3 sm:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">New Entry</span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-4 rounded-2xl shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <p className="text-red-700 font-medium text-sm sm:text-base">{error}</p>
            </div>
          </div>
        )}

        {/* New Entry Form */}
        {showNewEntryForm && (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-4 sm:p-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex-shrink-0">
                  <PenTool className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                  {editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}
                </h2>
              </div>
              <button
                onClick={handleCancelForm}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200 flex-shrink-0"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveEntry} className="space-y-4 sm:space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
                    formErrors.title ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  placeholder="Enter a title for your journal entry"
                  maxLength={200}
                />
                {formErrors.title && (
                  <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{formErrors.title}</span>
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500 flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formData.title.length}/200 characters</span>
                </p>
              </div>

              {/* Content */}
              <div>
                <label htmlFor="content" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Content *
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleFormChange}
                  rows={8}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 text-sm sm:text-base ${
                    formErrors.content ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  placeholder="Write your thoughts, feelings, and experiences..."
                  maxLength={10000}
                />
                {formErrors.content && (
                  <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{formErrors.content}</span>
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500 flex items-center space-x-1">
                  <PenTool className="w-3 h-3" />
                  <span>{formData.content.length}/10,000 characters</span>
                </p>
              </div>

              {/* Mood */}
              <div>
                <label htmlFor="mood" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  How are you feeling?
                </label>
                <select
                  id="mood"
                  name="mood"
                  value={formData.mood}
                  onChange={handleFormChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
                >
                  <option value="very-low">😔 Very Low</option>
                  <option value="low">😕 Low</option>
                  <option value="neutral">😐 Neutral</option>
                  <option value="good">🙂 Good</option>
                  <option value="excellent">😊 Excellent</option>
                </select>
              </div>

              {/* Tags */}
              <div>
                <label htmlFor="tags" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Tags (optional)
                </label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleFormChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
                  placeholder="Enter tags separated by commas (e.g., therapy, anxiety, gratitude)"
                />
                <p className="mt-2 text-xs text-gray-500 flex items-center space-x-1">
                  <Tag className="w-3 h-3" />
                  <span>Tags help you organize and find your entries later</span>
                </p>
              </div>

              {/* Privacy */}
              <div className="flex items-center space-x-3 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                <input
                  type="checkbox"
                  id="isPrivate"
                  name="isPrivate"
                  checked={formData.isPrivate}
                  onChange={handleFormChange}
                  className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isPrivate" className="flex items-center space-x-2 text-sm text-gray-700">
                  {formData.isPrivate ? (
                    <>
                      <Lock className="w-4 h-4 text-blue-600" />
                      <span>Keep this entry private</span>
                    </>
                  ) : (
                    <>
                      <Unlock className="w-4 h-4 text-purple-600" />
                      <span>Make this entry public</span>
                    </>
                  )}
                </label>
              </div>

              {/* Submit Error */}
              {formErrors.submit && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-600 flex items-center space-x-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{formErrors.submit}</span>
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-4 sm:pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 transform hover:scale-105 font-medium text-sm sm:text-base"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  {formLoading ? (
                    <>
                      <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>{editingEntry ? 'Update Entry' : 'Save Entry'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Journal Entries */}
        {entries.length === 0 ? (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl mb-4 sm:mb-6">
              <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No journal entries yet</h3>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto">Start your journaling journey by creating your first entry. Reflect on your thoughts and get AI-powered insights.</p>
            <button 
              onClick={() => setShowNewEntryForm(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2 mx-auto text-sm sm:text-base"
            >
              <PenTool className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Write Your First Entry</span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {entries.map((entry) => (
              <div key={entry._id} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100 rounded-3xl p-4 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                {/* Entry Header - Mobile Responsive */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                      <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 leading-tight">{entry.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600">
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <div className="p-1 bg-blue-100 rounded-lg">
                            <Calendar className="w-3 h-3 text-blue-600" />
                          </div>
                          <span className="font-medium">{formatDate(entry.createdAt)}</span>
                        </div>
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <span className="text-base sm:text-lg">{getMoodIcon(entry.mood)}</span>
                          <span className="font-medium capitalize">{entry.mood.replace('-', ' ')}</span>
                        </div>
                        {entry.tags && entry.tags.length > 0 && (
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <div className="p-1 bg-purple-100 rounded-lg">
                              <Tag className="w-3 h-3 text-purple-600" />
                            </div>
                            <span className="font-medium">{entry.tags.length} tag{entry.tags.length !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          {entry.isPrivate ? (
                            <>
                              <div className="p-1 bg-red-100 rounded-lg">
                                <EyeOff className="w-3 h-3 text-red-600" />
                              </div>
                              <span className="font-medium">Private</span>
                            </>
                          ) : (
                            <>
                              <div className="p-1 bg-green-100 rounded-lg">
                                <Eye className="w-3 h-3 text-green-600" />
                              </div>
                              <span className="font-medium">Public</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 self-end sm:self-start">
                    <button 
                      onClick={() => handleEditEntry(entry)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 transform hover:scale-105"
                      title="Edit entry"
                    >
                      <Edit3 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteEntry(entry._id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 transform hover:scale-105"
                      title="Delete entry"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>

                {/* Entry Content */}
                <div className="mb-4 sm:mb-6">
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-lg">
                    {truncateContent(entry.content)}
                  </p>
                  {entry.content.length > 150 && (
                    <button className="text-sm text-blue-600 hover:text-blue-700 mt-2 sm:mt-3 font-medium flex items-center space-x-1">
                      <span>Read more</span>
                      <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  )}
                </div>

                {/* Tags */}
                {entry.tags && entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                    {entry.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 sm:px-3 py-1 rounded-xl text-xs bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200 font-medium"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* AI Insights */}
                <JournalInsights
                  entry={entry}
                  insights={insights[entry._id]}
                  onGenerateInsights={handleGenerateInsights}
                  loading={generatingInsights[entry._id]}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Journal; 